import React, { useState, useEffect, useRef, useCallback } from "react";
import { quizSections } from "./QuizWeights";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import QuestionText from "./QuestionText";

const NeuroAgeLifestyleQuiz = () => {
  // Add a new state to track whether the user has entered their email
  const [emailEntered, setEmailEntered] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [emailError, setEmailError] = useState("");

  // Your existing states
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  // New states for better navigation
  const [selectedOption, setSelectedOption] = useState(null);
  const [showSectionTransition, setShowSectionTransition] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  // Add a state to track if we've saved to Firebase already
  const [savedToFirebase, setSavedToFirebase] = useState(false);
  // Add a ref to prevent multiple submissions while one is in progress
  const isSubmittingRef = useRef(false);

  // Function to validate email
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Function to handle email submission
  const handleEmailSubmit = (e) => {
    e.preventDefault();

    // Reset error state
    setEmailError("");

    // Validate email
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    // If validation passes, proceed to the quiz
    setEmailEntered(true);

    // Here you could also save the email to your database or state management
    console.log("Collected email:", email);
    console.log("Collected name:", name);
  };

  // Calculate score for a question using the new point value format - wrapped in useCallback
  const calculateQuestionScore = useCallback((question, answer) => {
    // Find the matching option in the question's options array
    const option = question.options.find((opt) => opt[0] === answer);

    // If found, return the point value (second element in the option array)
    if (option) {
      return option[1];
    }

    // Default to 0 if no match is found
    return 0;
  }, []);

  // Calculate section score - wrapped in useCallback
  const calculateSectionScore = useCallback(
    (sectionIndex) => {
      const section = quizSections[sectionIndex];
      let totalScore = 0;
      let totalWeight = 0;

      section.questions.forEach((question) => {
        const answer = answers[question.id];
        if (answer !== undefined) {
          const score = calculateQuestionScore(question, answer);
          totalScore += score * question.weight;
          totalWeight += question.weight;
        }
      });

      return totalWeight > 0 ? totalScore / totalWeight : 0;
    },
    [answers, calculateQuestionScore]
  );

  // Calculate overall score - wrapped in useCallback
  const calculateOverallScore = useCallback(() => {
    // Define section weights based on scientific evidence
    const sectionWeights = [
      0.25, // Diet (25%)
      0.25, // Exercise (25%)
      0.2, // Sleep (20%)
      0.15, // Community (15%)
      0.15, // Mental Activity (15%)
      0.15, // Stress Management (15%)
      0.15, // General Health (15%)
      0.15, // Mental Health (15%)
      0.05, // Environmental Factors (5%)
    ];

    let weightedScore = 0;
    let totalWeight = 0;

    quizSections.forEach((section, index) => {
      const sectionScore = calculateSectionScore(index);
      weightedScore += sectionScore * sectionWeights[index];
      totalWeight += sectionWeights[index];
    });

    return totalWeight > 0 ? weightedScore / totalWeight : 0;
  }, [calculateSectionScore]);

  // Get score category for viral sharing
  const getScoreCategory = (score) => {
    if (score >= 8.5)
      return {
        name: "NeuroAge Lifestyle Virtuoso",
        emoji: "🧠⭐",
        color: "text-green-700",
      };
    if (score >= 7.5)
      return {
        name: "NeuroAge Lifestyle Champion",
        emoji: "🧠🏆",
        color: "text-green-600",
      };
    if (score >= 6.5)
      return {
        name: "NeuroAge Lifestyle Achiever",
        emoji: "🧠✨",
        color: "text-green-500",
      };
    if (score >= 5.5)
      return {
        name: "NeuroAge Lifestyle Apprentice", //rising star?
        emoji: "🧠🔨",
        color: "text-yellow-600",
      };
    if (score >= 4.5)
      return {
        name: "NeuroAge Lifestyle Explorer",
        emoji: "🧠🗺️",
        color: "text-yellow-500",
      };
    return {
      name: "NeuroAge Lifestyle Novice",
      emoji: "🧠🌱",
      color: "text-orange-500",
    };
  };

  // Handle option selection and automatically advance to next question
  const handleOptionSelect = (questionId, value) => {
    setSelectedOption(value);
    setAnswers({
      ...answers,
      [questionId]: value,
    });

    // Automatically advance to the next question after selecting an option
    setTimeout(() => {
      setSelectedOption(null);
      nextQuestion();
    }, 400); // Slightly longer for better visual feedback
  };

  // Navigate to next question
  const nextQuestion = () => {
    const section = quizSections[currentSection];
    if (currentQuestion < section.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < quizSections.length - 1) {
      // Show section transition
      setShowSectionTransition(true);
      setTimeout(() => {
        setShowSectionTransition(false);
        setCurrentSection(currentSection + 1);
        setCurrentQuestion(0);
      }, 1200);
    } else {
      setShowResults(true);
    }
  };

  // Navigate to previous question
  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(quizSections[currentSection - 1].questions.length - 1);
    }
  };

  // Jump to specific section (for tab navigation)
  const jumpToSection = (sectionIndex) => {
    if (sectionIndex >= 0 && sectionIndex < quizSections.length) {
      setCurrentSection(sectionIndex);
      setCurrentQuestion(0);
    }
  };

  // Get completed sections for tab display
  const getCompletedSections = () => {
    return quizSections.map((section, index) => {
      const sectionQuestions = section.questions;
      const answeredQuestions = sectionQuestions.filter(
        (q) => answers[q.id] !== undefined
      );
      return {
        index,
        title: section.title,
        completed: answeredQuestions.length === sectionQuestions.length,
        progress: (answeredQuestions.length / sectionQuestions.length) * 100,
      };
    });
  };

  // Current question data
  const currentSectionData = quizSections[currentSection];
  const currentQuestionData = currentSectionData?.questions[currentQuestion];
  const currentQuestionId = currentQuestionData?.id;
  const totalQuestions = quizSections.reduce(
    (acc, section) => acc + section.questions.length,
    0
  );
  const questionsAnswered = Object.keys(answers).length;

  // Effect to save scores to Firebase when results are shown
  useEffect(() => {
    // Function to save scores to Firebase
    const saveScoresToFirebase = async () => {
      // Prevent multiple submissions while one is in progress
      if (isSubmittingRef.current) return;

      // Only save if showing results and haven't saved yet
      if (showResults && !savedToFirebase) {
        try {
          isSubmittingRef.current = true;
          const overallScore = calculateOverallScore();
          const sectionScores = quizSections.map((section, index) => ({
            title: section.title,
            score: calculateSectionScore(index),
          }));

          // Format the data to save
          const scoreData = {
            name: name,
            email: email,
            timestamp: serverTimestamp(),
            overall: overallScore,
            sectionScores: sectionScores.reduce((obj, section) => {
              obj[section.title.replace(/\s+/g, "")] = section.score;
              return obj;
            }, {}),
            answers: answers,
          };

          // Add the document to the quiz-scores collection
          await addDoc(collection(db, "quiz-scores"), scoreData);
          console.log("Scores saved to Firebase successfully");
          setSavedToFirebase(true);
        } catch (error) {
          console.error("Error saving scores to Firebase:", error);
        } finally {
          isSubmittingRef.current = false;
        }
      }
    };

    saveScoresToFirebase();
  }, [
    showResults,
    savedToFirebase,
    name,
    email,
    answers,
    calculateOverallScore,
    calculateSectionScore,
  ]);
  

  // Generate sample social media text
  const generateSocialMediaText = () => {
    const score = Math.round(calculateOverallScore() * 10) / 10;
    const category = getScoreCategory(score);
    const baseUrl = window.location.origin;

    // Shorter text for Twitter/X that fits within character limit
    const twitterText = `🧠 I'm a ${category.name}! Scored ${score}/10 on the NeuroAge Lifestyle Quiz. How do you measure up? Take the quiz: ${baseUrl} #BrainHealth #NeuroAge`;

    // Full text for other platforms that don't have strict character limits
    const fullText = `🧠 I just achieved "${category.name}" ${category.emoji} on the NeuroAge Lifestyle Quiz!
  
  I scored ${score}/10 on the 9 pillars of brain health. This science-backed assessment evaluated my diet, exercise, sleep, stress management, and more.
  
  Curious how your lifestyle measures up? Take the quiz: ${baseUrl}
  
  #BrainHealth #NeuroAge #Longevity`;

    // Use appropriate text based on platform
    return { twitterText, fullText };
  };

  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log("Text copied to clipboard");
    });
  };

// Generate social media image
const generateSocialImage = (preview = false) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  // Set canvas size for optimal social media sharing (1200x630 for Facebook/Twitter)
  canvas.width = 1200;
  canvas.height = 630;
  
  // Set image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#065f46'); // dark green
  gradient.addColorStop(1, '#047857'); // slightly lighter green
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Add subtle pattern overlay
  ctx.globalAlpha = 0.05;
  for (let i = 0; i < canvas.width; i += 60) {
    for (let j = 0; j < canvas.height; j += 60) {
      ctx.beginPath();
      ctx.arc(i, j, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
  
  // Title
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 40px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('My NeuroAge Lifestyle Score', canvas.width / 2, 55);
  
  // URL
  ctx.font = '24px Arial, sans-serif';
  ctx.fillStyle = '#86efac';
  ctx.fillText('quiz.neuroagetx.com', canvas.width / 2, 90);
  
  // Main score section
  const score = calculateOverallScore();
  const category = getScoreCategory(score);
  
  // Score display area
  const scoreAreaY = 120;
  
  // Score circle
  const circleX = 300;
  const circleY = scoreAreaY + 60;
  const radius = 55;
  
  // Draw outer circle
  ctx.beginPath();
  ctx.arc(circleX, circleY, radius + 5, 0, Math.PI * 2);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 3;
  ctx.stroke();
  
  // Draw inner filled circle
  ctx.beginPath();
  ctx.arc(circleX, circleY, radius, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  
  // Score text
  ctx.fillStyle = '#065f46';
  ctx.font = 'bold 48px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(Math.round(score * 10) / 10, circleX, circleY - 5);
  
  ctx.font = '18px Arial, sans-serif';
  ctx.fillText('/10', circleX, circleY + 18);
  
  // Category text
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  ctx.font = 'bold 32px Arial, sans-serif';
  ctx.fillText(`I'm a ${category.name}!`, 400, circleY);
  
  // Get all pillar scores in original order
  const sectionScores = quizSections.map((section, index) => ({
    title: section.title,
    score: calculateSectionScore(index),
  }));
  
  // Pillar scores header
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 26px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('My 9 Pillars of Brain Health', canvas.width / 2, 260);
  
  // Draw pillar scores
  const startY = 295;
  const pillarSpacing = 44; // Increased from 36
  const barMaxWidth = 380; // Slightly reduced to fit better
  const leftColumnX = 150;
  const rightColumnX = 650;
  
  sectionScores.forEach((section, index) => {
    const isLeftColumn = index < 5;
    const columnX = isLeftColumn ? leftColumnX : rightColumnX;
    const columnIndex = isLeftColumn ? index : index - 5;
    const y = startY + (columnIndex * pillarSpacing);
    
    // Pillar name
    ctx.font = '20px Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.fillText(section.title, columnX, y);
    
    // Score bar background - positioned with more space from text
    const barX = columnX;
    const barY = y + 8; // Increased from 5 to give more space
    const barHeight = 16; // Slightly reduced from 18
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fillRect(barX, barY, barMaxWidth, barHeight);
    
    // Score bar fill - using full gradient spectrum
    const fillWidth = (section.score / 10) * barMaxWidth;
    const barGradient = ctx.createLinearGradient(barX, 0, barX + fillWidth, 0);
    
    if (section.score >= 9) {
      // Deep green for excellent scores
      barGradient.addColorStop(0, '#059669');
      barGradient.addColorStop(1, '#10b981');
    } else if (section.score >= 8) {
      // Green to light green
      barGradient.addColorStop(0, '#10b981');
      barGradient.addColorStop(1, '#34d399');
    } else if (section.score >= 7) {
      // Light green to teal
      barGradient.addColorStop(0, '#34d399');
      barGradient.addColorStop(1, '#14b8a6');
    } else if (section.score >= 6) {
      // Teal to blue
      barGradient.addColorStop(0, '#14b8a6');
      barGradient.addColorStop(1, '#3b82f6');
    } else if (section.score >= 5) {
      // Blue to purple
      barGradient.addColorStop(0, '#3b82f6');
      barGradient.addColorStop(1, '#8b5cf6');
    } else if (section.score >= 4) {
      // Purple to pink
      barGradient.addColorStop(0, '#8b5cf6');
      barGradient.addColorStop(1, '#ec4899');
    } else if (section.score >= 3) {
      // Pink to orange
      barGradient.addColorStop(0, '#ec4899');
      barGradient.addColorStop(1, '#f97316');
    } else if (section.score >= 2) {
      // Orange to red-orange
      barGradient.addColorStop(0, '#f97316');
      barGradient.addColorStop(1, '#ef4444');
    } else {
      // Red gradient for lowest scores
      barGradient.addColorStop(0, '#ef4444');
      barGradient.addColorStop(1, '#dc2626');
    }
    
    ctx.fillStyle = barGradient;
    ctx.fillRect(barX, barY, fillWidth, barHeight);
    
    // Score text (positioned to the right of the bar with padding)
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.font = 'bold 20px Arial, sans-serif';
    ctx.fillText(Math.round(section.score * 10) / 10, barX + barMaxWidth + 15, y);
  });
  
  // Footer message
  ctx.fillStyle = '#86efac';
  ctx.font = '22px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Take the quiz and discover your lifestyle score!', canvas.width / 2, 570);
  
  // Add date and name
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.font = '16px Arial, sans-serif';
  ctx.textAlign = 'left';
  if (name) {
    ctx.fillText(`${name} • ${new Date().toLocaleDateString()}`, 100, canvas.height - 20);
  } else {
    ctx.fillText(new Date().toLocaleDateString(), 100, canvas.height - 20);
  }
  
  // Branding
  ctx.textAlign = 'right';
  ctx.fillText('Powered by NeuroAge', canvas.width - 100, canvas.height - 20);
  
  if (preview) {
    // Show preview
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      setPreviewImageUrl(url);
      setShowImagePreview(true);
    }, 'image/png');
  } else {
    // Convert canvas to blob and download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `NeuroAge_Results_${name.replace(/\s+/g, '_')}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }
};

  // Handle retaking quiz
  const handleRetakeQuiz = () => {
    setShowResults(false);
    setCurrentSection(0);
    setCurrentQuestion(0);
    setSavedToFirebase(false);
    setAnswers({});
  };

  // TEST FUNCTION - Uncomment for development testing
  // const fillRandomAnswersAndShowResults = () => {
  //   const randomAnswers = {};
  //   quizSections.forEach((section) => {
  //     section.questions.forEach((question) => {
  //       const randomIndex = Math.floor(Math.random() * question.options.length);
  //       randomAnswers[question.id] = question.options[randomIndex][0];
  //     });
  //   });
  //   setAnswers(randomAnswers);
  //   setShowResults(true);
  // };

  //Render email form
  const renderEmailForm = () => {
    return (
      <div className="bg-white rounded-lg p-8 shadow-lg max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014.846 21H9.154a3.374 3.374 0 00-2.169-1.953l-.548-.547z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-green-700 mb-2">
            Discover Your Lifestyle Score
          </h2>
          <p className="text-gray-600">
            Take our 4-minute assessment to learn how your lifestyle supports
            healthy brain aging.
          </p>
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Your Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Enter your full name"
              required
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Enter your email address"
              required
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-600">{emailError}</p>
            )}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              Start Assessment →
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Get your results instantly. No spam, unsubscribe anytime.
          </p>
        </form>
      </div>
    );
  };

  // Render section transition screen
  const renderSectionTransition = () => {
    const nextSection = quizSections[currentSection + 1];
    const completedSection = quizSections[currentSection];

    return (
      <div className="bg-white rounded-lg p-8 shadow-lg max-w-2xl mx-auto text-center">
        <div className="mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-green-700 mb-2">
            {completedSection.title} ✓
          </h2>
          <p className="text-gray-600">
            Section complete! Moving to {nextSection?.title || "Results"}...
          </p>
        </div>

        <div className="mt-6">
          <div className="animate-pulse flex justify-center">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render section tabs
  const renderSectionTabs = () => {
    const completedSections = getCompletedSections();

    return (
      <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
        <div className="flex overflow-x-auto">
          {completedSections.map((section) => (
            <button
              key={section.index}
              onClick={() => jumpToSection(section.index)}
              className={`flex-1 min-w-0 px-3 py-3 text-xs font-medium transition-colors relative ${
                section.index === currentSection
                  ? "bg-green-50 text-green-700 border-b-2 border-green-500"
                  : section.completed
                  ? "bg-gray-50 text-gray-700 hover:bg-green-25"
                  : section.progress > 0
                  ? "bg-yellow-25 text-gray-600 hover:bg-yellow-50"
                  : "text-gray-500 hover:bg-gray-25"
              }`}
            >
              <div className="truncate">{section.title}</div>
              {section.completed && (
                <div className="absolute top-1 right-1">
                  <svg
                    className="w-3 h-3 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render the quiz interface
  const renderQuiz = () => {
    if (showSectionTransition) {
      return renderSectionTransition();
    }

    return (
      <div className="max-w-3xl mx-auto">
        {/* Section Tabs */}
        {renderSectionTabs()}

        {/* Main Quiz Card */}
        <div className="bg-white rounded-lg p-6 shadow-lg">
          {/* Header with single progress bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-2xl font-bold text-green-700">
                  {currentSectionData.title}
                </h2>
                <p className="text-sm text-gray-500">
                  Question {currentQuestion + 1} of{" "}
                  {currentSectionData.questions.length}
                </p>
              </div>
              <div className="text-right">
                <span className="text-lg font-semibold text-gray-700">
                  {questionsAnswered} / {totalQuestions}
                </span>
                <p className="text-xs text-gray-500">Overall Progress</p>
              </div>
            </div>

            {/* Single progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500 ease-out"
                style={{
                  width: `${(questionsAnswered / totalQuestions) * 100}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Question */}
          <div className="mb-8">
            <QuestionText text={currentQuestionData.text} />

            {/* Options */}
            <div className="space-y-3">
              {currentQuestionData.options.map((option, index) => (
                <button
                  key={option[0]}
                  onClick={() =>
                    handleOptionSelect(currentQuestionId, option[0])
                  }
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                    selectedOption === option[0]
                      ? "bg-green-100 border-green-500 scale-102"
                      : answers[currentQuestionId] === option[0]
                      ? "bg-green-50 border-green-300"
                      : "border-gray-200 hover:border-green-300 hover:bg-green-25"
                  } ${
                    selectedOption !== null
                      ? "cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  disabled={selectedOption !== null}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center transition-all ${
                        selectedOption === option[0] ||
                        answers[currentQuestionId] === option[0]
                          ? "border-green-500 bg-green-500"
                          : "border-gray-300"
                      }`}
                    >
                      {(selectedOption === option[0] ||
                        answers[currentQuestionId] === option[0]) && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span className="text-base">{option[0]}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Auto-advance indicator */}
            {selectedOption && (
              <div className="mt-6 text-center">
                <div className="inline-flex items-center text-green-600 bg-green-50 px-4 py-2 rounded-full">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="text-sm font-medium">
                    Moving to next question...
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <button
              onClick={prevQuestion}
              disabled={currentSection === 0 && currentQuestion === 0}
              className={`flex items-center px-4 py-2 rounded-lg transition-all ${
                currentSection === 0 && currentQuestion === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>
            
            {/* TEST BUTTON - Comment out this entire button block for production 
            <button
              onClick={fillRandomAnswersAndShowResults}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
              title="Fill random answers and skip to results (for testing)"
            >
              🧪 Test
            </button>
            END TEST BUTTON */}

            <button
              onClick={nextQuestion}
              disabled={answers[currentQuestionId] === undefined}
              className={`flex items-center px-6 py-2 rounded-lg transition-all ${
                answers[currentQuestionId] === undefined
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {currentSection === quizSections.length - 1 &&
              currentQuestion === currentSectionData.questions.length - 1
                ? "View Results"
                : "Next"}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
          <div className="text-center mt-8 pt-4 border-t border-gray-200 text-sm text-gray-500">
            <p>
              NeuroAge{" "}
              
                <a href="https://www.neuroagetx.com/neuprint"
                className="text-green-600 hover:underline"
              >
                Neuprint™
              </a>{" "}
              |{" "}
              
                <a href="https://www.neuroagetx.com"
                className="text-green-600 hover:underline"
              >
                Visit Main Site
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Render the quiz results
  const renderResults = () => {
    const overallScore = calculateOverallScore();
    const category = getScoreCategory(overallScore);
    const sectionScores = quizSections.map((section, index) => ({
      title: section.title,
      score: calculateSectionScore(index),
    }));
    // Helper function to get the detailed page anchor link for each pillar
    // const getPillarLink = (title) => {
    //   const anchorMap = {
    //     'Diet': 'diet',
    //     'Exercise': 'exercise',
    //     'Sleep': 'sleep',
    //     'Community': 'community',
    //     'Mental Activity': 'mental-activity',
    //     'Stress Management': 'stress',
    //     'General Health': 'general-health',
    //     'Mental Health': 'mental-health',
    //     'Environmental Factors': 'environmental-toxins'
    //   };
    //   return `https://www.neuroagetx.com/neuprint/detailed#${anchorMap[title]}`;
    // };

    const getPillarAdvice = (title, score) => {
      if (score >= 8)
        return `Excellent! Your ${title.toLowerCase()} habits are optimal for brain health.`;
      if (score >= 6)
        return `Good foundation. Small improvements in ${title.toLowerCase()} could boost your brain health.`;
      if (score >= 4)
        return `Focus area identified. Improving ${title.toLowerCase()} habits will significantly benefit your brain.`;
      return `Priority area. Start with small changes to your ${title.toLowerCase()} routine.`;
    };

    return (
      <div className="bg-white rounded-lg p-8 shadow-lg max-w-4xl mx-auto">
        {/* Main Score Display */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <span className="text-5xl">{category.emoji}</span>
          </div>
          <h2 className="text-3xl font-bold text-green-700 mb-2">
            You're a {category.name}!
          </h2>
          <div className="text-6xl font-bold mb-2 text-green-800">
            {Math.round(overallScore * 10) / 10}
          </div>
          <p className="text-xl text-gray-600">out of 10</p>

          <div className="mt-6 p-4 bg-green-50 rounded-lg max-w-2xl mx-auto">
            {overallScore >= 8 ? (
              <p className="text-green-800">
                Outstanding! Your lifestyle habits are exceptionally supportive
                of healthy brain aging. Keep up the excellent work! 
              </p>
            ) : overallScore >= 6 ? (
              <p className="text-green-700">
                Great job! You're on the right track with habits that support
                brain health. A few tweaks could make you even stronger.
              </p>
            ) : overallScore >= 4 ? (
              <p className="text-yellow-700">
                You're building a foundation for brain health. Focus on the
                areas below to give your brain the best support.
              </p>
            ) : (
              <p className="text-orange-700">
                There's significant room for improvement. The good news? Small
                changes in these areas can make a big difference for your brain
                health.
              </p>
            )}
          </div>
        </div>

        {/* Pillar Scores */}
        <div className="mb-8">
          <h3 className="text-2xl font-semibold mb-6 text-center text-green-700">
            Your Brain Health Breakdown
          </h3>
          <p className="text-center text-gray-600 mb-6">
           Click on any pillar below to learn more about improving that aspect of your brain health
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sectionScores.map((section) => (
              <div
                key={section.title}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-medium">{section.title}</h4>
                  <span
                    className={`text-2xl font-bold ${
                      section.score >= 8
                        ? "text-green-600"
                        : section.score >= 6
                        ? "text-green-500"
                        : section.score >= 4
                        ? "text-yellow-500"
                        : "text-orange-500"
                    }`}
                  >
                    {Math.round(section.score * 10) / 10}
                  </span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      section.score >= 8
                        ? "bg-green-600"
                        : section.score >= 6
                        ? "bg-green-500"
                        : section.score >= 4
                        ? "bg-yellow-500"
                        : "bg-orange-500"
                    }`}
                    style={{ width: `${section.score * 10}%` }}
                  ></div>
                </div>

                <p className="text-sm text-gray-600">
                  {getPillarAdvice(section.title, section.score)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Social Sharing Section */}
        <div className="border-t pt-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Share Your Results
            </h3>
            <p className="text-gray-600 mb-2">
              Inspire others to check their brain health!
            </p>
            <p className="text-sm text-gray-500">
              Create a beautiful image to share on social media or share with text
            </p>
          </div>

          {/* Share Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <button
              onClick={() => {
                const { fullText } = generateSocialMediaText();
                window.open(
                  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    window.location.origin
                  )}&quote=${encodeURIComponent(fullText)}`,
                  "_blank"
                );
              }}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z"
                  clipRule="evenodd"
                />
              </svg>
              Facebook
            </button>

            <button
              onClick={() => {
                const { twitterText } = generateSocialMediaText();
                window.open(
                  `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    twitterText
                  )}`,
                  "_blank"
                );
              }}
              className="flex items-center px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
              </svg>
              Twitter/X
            </button>

            <button
              onClick={() => {
                const { fullText } = generateSocialMediaText();
                window.open(
                  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                    window.location.origin
                  )}&summary=${encodeURIComponent(fullText)}`,
                  "_blank"
                );
              }}
              className="flex items-center px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors font-medium"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                  clipRule="evenodd"
                />
              </svg>
              LinkedIn
            </button>

            <button
              onClick={() => generateSocialImage(true)}
              className="flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Share Image
            </button>

            <button
              onClick={() => {
                const { fullText } = generateSocialMediaText();
                copyToClipboard(fullText);
              }}
              className="flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copy Text
            </button>
          </div>

          {/* Sample Share Text */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">Sample Share Text</h4>
                <button
                  onClick={() => {
                    const { fullText } = generateSocialMediaText();
                    copyToClipboard(fullText);
                  }}
                  className="text-green-600 hover:text-green-800 text-sm"
                >
                  📋 Copy
                </button>
              </div>
              <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 max-h-32 overflow-y-auto">
                {generateSocialMediaText().fullText}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="text-center">
            <div className="mb-6">
              <button
                onClick={() =>
                  (window.location.href = "https://www.neuroagetx.com/neuprint")
                }
                className="px-8 py-4 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 transition-colors text-lg"
              >
                📧 Get Personalized Coaching
              </button>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={handleRetakeQuiz}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                🔄 Retake Quiz
              </button>
            </div>
          </div>
        </div>

        {/* Image Preview Modal */}
        {showImagePreview && previewImageUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">Share Image Preview</h3>
                <button
                  onClick={() => {
                    setShowImagePreview(false);
                    if (previewImageUrl) {
                      URL.revokeObjectURL(previewImageUrl);
                      setPreviewImageUrl(null);
                    }
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                <img 
                  src={previewImageUrl} 
                  alt="Social media share preview" 
                  className="w-full h-auto rounded-lg shadow-md"
                />
                <div className="mt-4 flex justify-center gap-3">
                  <button
                    onClick={() => generateSocialImage(false)}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Download Image
                  </button>
                  <button
                    onClick={() => {
                      setShowImagePreview(false);
                      if (previewImageUrl) {
                        URL.revokeObjectURL(previewImageUrl);
                        setPreviewImageUrl(null);
                      }
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render appropriate content based on quiz state
  return (
    <div className="min-h-screen bg-green-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          
            <a href="https://www.neuroagetx.com"
            className="inline-block hover:opacity-90 transition-opacity"
          >
            <h1 className="text-4xl font-bold text-green-800 mb-2">
              NeuroAge Neuprint™
            </h1>
            <p className="text-xl text-green-700">
              Discover your Neuprint Lifestyle score in 4 minutes
            </p>
          </a>
        </div>

        {!emailEntered
          ? // Show email form if email hasn't been entered
            renderEmailForm()
          : showResults
          ? // Show results if quiz is completed
            renderResults()
          : // Show quiz questions
            renderQuiz()}
      </div>
    </div>
  );
};

export default NeuroAgeLifestyleQuiz;